import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Download } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { sendInvite } from "@/lib/alertsApi";

const INK = "#141412";
const stripFlag = (s) => (s || "").replace(/\s*🇨🇦\s*$/u, "").trim();
const LAYER_UI = { A: "Physicians / Rendering Providers" };
// Temporarily hidden while the API moves to friendlier module names. Flip to true to re-enable.
const MODULES_ENABLED = false;
const hasFlag = (s) => /🇨🇦/u.test(s || "");
const etFmt = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { timeZone: "America/New_York" }) + " ET"; }
  catch { return ts; }
};

function Shell({ title, sub, onClose, children, wide, testid }) {
  return (
    <div data-testid={testid} style={{ position: "fixed", inset: 0, zIndex: 9200, background: "rgba(20,20,18,.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 18, width: wide ? "min(760px,96vw)" : "min(520px,96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 30px 80px rgba(20,20,18,.3)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "18px 22px 14px", borderBottom: "1px solid #EEEEEC" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>{title}</div>
            {sub ? <div style={{ fontSize: 12.5, color: "#6C6C69", marginTop: 3, lineHeight: 1.45 }}>{sub}</div> : null}
          </div>
          <button onClick={onClose} data-testid="ml-modal-close" aria-label="Close" style={{ color: "#9B9B97", background: "transparent", border: 0, cursor: "pointer", padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ padding: "18px 22px 20px", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

const field = { width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1px solid #E2E2DF", borderRadius: 10, fontSize: 13.5, outline: "none" };
const label = { fontSize: 11.5, fontWeight: 700, color: INK, marginBottom: 6, display: "block" };
const primaryBtn = (on) => ({ background: on ? INK : "#E4E4E1", color: on ? "#fff" : "#9B9B97", border: 0, borderRadius: 999, padding: "11px 20px", fontWeight: 800, fontSize: 13.5, cursor: on ? "pointer" : "default", display: "inline-flex", alignItems: "center", gap: 8 });
const ghostBtn = { background: "transparent", border: 0, color: "#6C6C69", fontWeight: 700, fontSize: 13, cursor: "pointer" };

/* Create / Edit / Self-edit modal.
   mode: "create" | "edit" | "self" */
export function CreateEditModal({ mode, row, isPrimary, modules, taxonomy, onClose, onSaved }) {
  const self = mode === "self";
  const create = mode === "create";
  const [f, setF] = useState(() => ({
    first_name: row?.first_name || "",
    last_name: row?.last_name || "",
    email: row?.email || "",
    password: "",
    layer: row?.layer || "",
    credential: row?.credential || "",
    allowed_modules: row?.allowed_modules || [],
    can_manage_logins: !!row?.can_manage_logins,
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const layerGroups = useMemo(() => {
    const t = (taxonomy || []).find((x) => x.layer === f.layer);
    return t?.groups || [];
  }, [taxonomy, f.layer]);

  const setLayer = (L) => setF((s) => {
    const stillValid = (taxonomy || []).find((x) => x.layer === L)?.groups?.some((g) => g.items.some((it) => stripFlag(it) === s.credential));
    return { ...s, layer: L, credential: stillValid ? s.credential : "" };
  });

  const toggleModule = (key) => setF((s) => ({ ...s, allowed_modules: s.allowed_modules.includes(key) ? s.allowed_modules.filter((k) => k !== key) : [...s.allowed_modules, key] }));

  const save = async () => {
    setErr("");
    if (!f.layer) { setErr("Please choose a Layer (A, B or C)."); return; }
    if (create && (!f.email.trim() || !f.password.trim())) { setErr("Email and a temporary password are required."); return; }
    setBusy(true);
    try {
      if (self) {
        await api.patch("/api/edi/sub-logins/me/profile", { first_name: f.first_name, last_name: f.last_name, layer: f.layer, credential: f.credential || null });
      } else if (create) {
        await api.post("/api/edi/sub-logins", {
          first_name: f.first_name, last_name: f.last_name, email: f.email.trim(), password: f.password,
          layer: f.layer, credential: f.credential || null,
          ...(MODULES_ENABLED ? { allowed_modules: f.allowed_modules } : {}),
          ...(isPrimary ? { can_manage_logins: f.can_manage_logins } : {}),
        });
        try {
          await sendInvite({ email: f.email.trim(), first_name: f.first_name, last_name: f.last_name });
        } catch {
          toast.warning("User created, but the welcome email couldn't be sent.");
        }
      } else {
        await api.patch(`/api/edi/sub-logins/${row.id}`, {
          first_name: f.first_name, last_name: f.last_name,
          layer: f.layer, credential: f.credential || null,
          ...(MODULES_ENABLED ? { allowed_modules: f.allowed_modules } : {}),
          ...(isPrimary ? { can_manage_logins: f.can_manage_logins } : {}),
        });
      }
      toast.success(create ? "User created ✓" : "Saved ✓");
      onSaved();
    } catch (e) { setErr(describeError(e)); } finally { setBusy(false); }
  };

  return (
    <Shell testid="ml-create-modal" wide={!self}
      title={create ? "Add a user" : self ? "Edit your details" : `Edit ${row?.first_name || "user"}`}
      sub={self ? "Update your name and qualification. Modules and password aren't changed here." : create ? "They'll be asked to set their own password on first sign-in." : null}
      onClose={onClose}>
      {err ? <div data-testid="ml-modal-error" style={{ fontSize: 12.5, color: "#B3261E", background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "9px 11px", marginBottom: 14 }}>{err}</div> : null}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={label}>First name</label><input data-testid="ml-field-first" style={field} value={f.first_name} onChange={(e) => setF((s) => ({ ...s, first_name: e.target.value }))} /></div>
        <div><label style={label}>Last name</label><input data-testid="ml-field-last" style={field} value={f.last_name} onChange={(e) => setF((s) => ({ ...s, last_name: e.target.value }))} /></div>
      </div>

      {create ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
          <div><label style={label}>Email</label><input data-testid="ml-field-email" type="email" style={field} value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} /></div>
          <div><label style={label}>Temporary password</label><input data-testid="ml-field-password" style={field} value={f.password} onChange={(e) => setF((s) => ({ ...s, password: e.target.value }))} placeholder="They'll change it on first login" /></div>
        </div>
      ) : null}

      {/* Layer */}
      <div style={{ marginTop: 16 }}>
        <label style={label}>Layer <span style={{ color: "#B3261E" }}>*</span></label>
        <div style={{ display: "flex", gap: 8 }}>
          {["A", "B", "C"].map((L) => (
            <button key={L} type="button" data-testid={`ml-layer-${L}`} onClick={() => setLayer(L)}
              style={{ flex: 1, padding: "10px 8px", borderRadius: 10, border: `1.5px solid ${f.layer === L ? INK : "#E2E2DF"}`, background: f.layer === L ? INK : "#fff", color: f.layer === L ? "#fff" : INK, fontWeight: 700, fontSize: 12.5, cursor: "pointer", textAlign: "left" }}>
              Layer {L}
              <div style={{ fontWeight: 500, fontSize: 10.5, opacity: 0.8, marginTop: 2 }}>{LAYER_UI[L] || (taxonomy || []).find((x) => x.layer === L)?.label?.split("—")[1]?.trim() || ""}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Credential */}
      <div style={{ marginTop: 12 }}>
        <label style={label}>Credential (suffix){f.layer ? "" : " — pick a layer first"}</label>
        <select data-testid="ml-credential-select" disabled={!f.layer} style={{ ...field, background: f.layer ? "#fff" : "#F4F4F2" }}
          value={f.credential} onChange={(e) => setF((s) => ({ ...s, credential: e.target.value }))}>
          <option value="">— None —</option>
          {layerGroups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((it) => { const v = stripFlag(it); return <option key={it} value={v}>{v}{hasFlag(it) ? " · CA" : ""}</option>; })}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Modules (not on self-edit) */}
      {!self && MODULES_ENABLED ? (
        <div style={{ marginTop: 16 }}>
          <label style={label}>Module access</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxHeight: 200, overflowY: "auto", border: "1px solid #EEEEEC", borderRadius: 10, padding: 10 }}>
            {(modules || []).map((m) => (
              <label key={m.key} data-testid={`ml-module-${m.key}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: INK, cursor: "pointer", padding: "3px 4px" }}>
                <input type="checkbox" checked={f.allowed_modules.includes(m.key)} onChange={() => toggleModule(m.key)} />
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      {!self && !MODULES_ENABLED ? (
        <div data-testid="ml-modules-disabled" style={{ marginTop: 16, fontSize: 12, lineHeight: 1.5, color: "#6C6C69", background: "#F7F7F6", border: "1px solid #EEEEEC", borderRadius: 10, padding: "10px 12px" }}>
          Module access is being upgraded with clearer names and will be available here shortly. Existing access is unchanged in the meantime.
        </div>
      ) : null}

      {/* Allowed to add users — primary only, not self */}
      {isPrimary && !self ? (
        <label data-testid="ml-canmanage-toggle" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, fontSize: 13, color: INK, cursor: "pointer" }}>
          <input type="checkbox" checked={f.can_manage_logins} onChange={(e) => setF((s) => ({ ...s, can_manage_logins: e.target.checked }))} />
          <span><b>Allowed to add users</b> — this person can create and manage other logins for your tenant.</span>
        </label>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
        <button onClick={onClose} style={ghostBtn} data-testid="ml-cancel-btn">Cancel</button>
        <button onClick={save} disabled={busy} style={primaryBtn(!busy)} data-testid="ml-save-btn">{busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : create ? "Create user" : "Save changes"}</button>
      </div>
    </Shell>
  );
}

export function ResetModal({ row, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setErr("");
    if (pw.trim().length < 8) { setErr("Enter a temporary password of at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/api/edi/sub-logins/${row.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${row.first_name || row.email} ✓`);
      onSaved();
    } catch (e) { setErr(describeError(e)); } finally { setBusy(false); }
  };
  return (
    <Shell testid="ml-reset-modal" title={`Reset password · ${row.first_name || row.email}`} sub="They'll be required to set a new password on their next sign-in." onClose={onClose}>
      {err ? <div data-testid="ml-modal-error" style={{ fontSize: 12.5, color: "#B3261E", background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "9px 11px", marginBottom: 14 }}>{err}</div> : null}
      <label style={label}>Temporary password</label>
      <input data-testid="ml-reset-input" style={field} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
        <button onClick={onClose} style={ghostBtn} data-testid="ml-cancel-btn">Cancel</button>
        <button onClick={save} disabled={busy} style={primaryBtn(!busy)} data-testid="ml-reset-save">{busy ? <><Loader2 size={14} className="animate-spin" /> Resetting…</> : "Reset password"}</button>
      </div>
    </Shell>
  );
}

export function DeleteModal({ row, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setErr(""); setBusy(true);
    try { await api.delete(`/api/edi/sub-logins/${row.id}`); toast.success(`Deleted ${row.first_name || row.email}`); onDeleted(); }
    catch (e) { setErr(describeError(e)); setBusy(false); }
  };
  return (
    <Shell testid="ml-delete-modal" title="Delete this login?" sub={`${row.first_name || ""} ${row.last_name || ""} · ${row.email}`} onClose={onClose}>
      {err ? <div data-testid="ml-modal-error" style={{ fontSize: 12.5, color: "#B3261E", background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "9px 11px", marginBottom: 14 }}>{err}</div> : null}
      <p style={{ fontSize: 13, color: "#4A4A46", lineHeight: 1.55, margin: 0 }}>This permanently removes the login from your tenant. Their historical records stay intact, but they can no longer sign in. This can't be undone.</p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
        <button onClick={onClose} style={ghostBtn} data-testid="ml-cancel-btn">Cancel</button>
        <button onClick={go} disabled={busy} data-testid="ml-delete-confirm" style={{ background: "#B3261E", color: "#fff", border: 0, borderRadius: 999, padding: "11px 20px", fontWeight: 800, fontSize: 13.5, cursor: busy ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>{busy ? <><Loader2 size={14} className="animate-spin" /> Deleting…</> : "Delete login"}</button>
      </div>
    </Shell>
  );
}

export function ActivityModal({ row, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/api/edi/sub-logins/login-activity/${row.id}`).then((r) => setData(r.data)).catch((e) => setErr(describeError(e)));
  }, [row.id]);
  const S = ({ k, v }) => (
    <div style={{ flex: 1, background: "#F7F7F6", borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "#9B9B97", fontWeight: 700 }}>{k}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: INK, marginTop: 4 }}>{v?.total ?? 0}</div>
      <div style={{ fontSize: 11, color: "#6C6C69", marginTop: 2 }}>{v?.success ?? 0} ok · {v?.fail ?? 0} failed</div>
    </div>
  );
  return (
    <Shell testid="ml-activity-modal" wide title={`Login activity · ${row.first_name || row.email}`} sub={`Last login: ${etFmt(data?.last_login)}`} onClose={onClose}>
      {err ? <div style={{ fontSize: 12.5, color: "#B3261E" }}>{err}</div> : !data ? <div style={{ color: "#9B9B97", fontSize: 13 }}>Loading…</div> : (
        <>
          <div style={{ display: "flex", gap: 10 }}>
            <S k="Today" v={data.summary?.today} /><S k="Last 5 days" v={data.summary?.last_5_days} /><S k="All time" v={data.summary?.all_time} />
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: INK, margin: "18px 0 8px" }}>Recent attempts (last 50)</div>
          {(data.recent || []).length === 0 ? (
            <div data-testid="ml-activity-empty" style={{ fontSize: 13, color: "#9B9B97", padding: "14px 0" }}>No sign-in attempts recorded yet.</div>
          ) : (
            <div style={{ border: "1px solid #EEEEEC", borderRadius: 10, overflow: "hidden" }}>
              {data.recent.map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderTop: i ? "1px solid #F1F1EF" : "none", fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: a.success ? "#10b981" : "#ef4444", flex: "none" }} />
                  <span style={{ flex: 1, color: INK }}>{etFmt(a.timestamp)}</span>
                  <span style={{ color: "#6C6C69" }}>{a.ip_address || "—"}</span>
                  <span style={{ color: a.success ? "#1F7A46" : "#B3261E", fontWeight: 700, minWidth: 54, textAlign: "right" }}>{a.success ? "Success" : "Failed"}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

export function RosterModal({ tenantCode, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/api/edi/sub-logins/credential-roster").then((r) => setData(r.data)).catch((e) => setErr(describeError(e)));
  }, []);
  const exportCsv = () => {
    const rows = [["Tenant ID", "Layer", "Name", "Credential", "Email", "Role", "Status", "Modules", "Last login (ET)"]];
    (data.groups || []).forEach((g) => (g.members || []).forEach((m) => rows.push([
      data.tenant_code ?? tenantCode ?? "", g.layer || "Unassigned", m.name || "", m.credential || "",
      m.email || "", m.is_primary ? "Primary" : "Sub-login", m.is_active ? "Active" : "Disabled",
      m.module_count ?? "", etFmt(m.last_login),
    ])));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `tenant-${data.tenant_code ?? "roster"}-credentials.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Shell testid="ml-roster-modal" wide title="Credential roster" sub={data ? `${data.total} people · Tenant ID ${data.tenant_code}` : "Loading…"} onClose={onClose}>
      {err ? <div style={{ fontSize: 12.5, color: "#B3261E" }}>{err}</div> : !data ? <div style={{ color: "#9B9B97", fontSize: 13 }}>Loading…</div> : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button onClick={exportCsv} data-testid="ml-roster-csv" style={{ ...primaryBtn(true), padding: "9px 16px" }}><Download size={14} /> Export CSV</button>
          </div>
          {(data.groups || []).map((g) => (
            <div key={g.layer || "none"} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: INK, marginBottom: 6 }}>{g.label} <span style={{ color: "#9B9B97", fontWeight: 600 }}>· {(g.members || []).length}</span></div>
              {(g.members || []).length === 0 ? <div style={{ fontSize: 12, color: "#C0C0BC" }}>No one yet.</div> : (
                <div style={{ border: "1px solid #EEEEEC", borderRadius: 10, overflow: "hidden" }}>
                  {g.members.map((m, i) => (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: i ? "1px solid #F1F1EF" : "none", fontSize: 12.5 }}>
                      <span style={{ flex: 1, color: INK, fontWeight: 600 }}>{m.name}{m.credential ? `, ${m.credential}` : ""}</span>
                      <span style={{ color: "#6C6C69", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{m.email}</span>
                      <span style={{ color: "#9B9B97" }}>{m.is_primary ? "Primary" : "Sub"}</span>
                      <span style={{ color: m.is_active ? "#1F7A46" : "#B3261E", fontWeight: 700 }}>{m.is_active ? "Active" : "Off"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </Shell>
  );
}
